import {
  AfterViewInit,
  Component,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import {NgForm} from '@angular/forms';
import {MatPaginator, PageEvent} from '@angular/material/paginator';
import {MatDialog} from '@angular/material/dialog';
import {ActivatedRoute, Router} from '@angular/router';
import {ReplaySubject} from 'rxjs';
import {delay, distinctUntilChanged, first, takeUntil, tap} from 'rxjs/operators';
import {select, Store} from '@ngrx/store';

import {CommissionTable, CommissionTableListItem} from '../../../../shared/interface/commission-table';
import {CommissionTableService} from '../../../../shared/services/commission-table.service';
import {FilterService} from '../../../../shared/services/filter.service';
import {AuthService} from '../../../../shared/services/auth.service';
import {UserPermissions} from '../../../../shared/interface/user-permissions';
import {CountedAndPaginated} from '../../../../shared/interface/response/counted-and-paginated';
import {StoreRootState} from '../../../../shared/ngrx/reducers';
import {getCurrentRouteState} from '../../../../shared/ngrx/selector';
import {RouterStateUrl} from '../../../../shared/ngrx/route-serializer';

@Component({
  selector: 'app-all-commission-tables-list',
  templateUrl: './all-commission-tables-list.component.html',
  styleUrls: ['./all-commission-tables-list.component.scss']
})
export class AllCommissionTablesListComponent
  implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('filtersForm') filtersForm: NgForm;
  @ViewChild('paginator') paginator: MatPaginator;

  initialFilters: any;
  initialized = false;

  items: CommissionTableListItem[] = [];
  totalData = 0;

  displayedColumns: string[] = [
    'shortName',
    'name',
    'validFrom',
    'validTo',
    'sourceTable',
    'assignments',
    'actions'
  ];

  userPermissions: ReplaySubject<UserPermissions>;

  private destroyed$: ReplaySubject<boolean>
    = new ReplaySubject<boolean>(1);

  constructor(
    private commissionTableService: CommissionTableService,
    private filterService: FilterService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private store: Store<StoreRootState>,
    private auth: AuthService,
    private dialog: MatDialog,
  ) {
    this.userPermissions = this.auth.getUserPermissions();

    this.commissionTableService.commissionTableListSubject
      .pipe(
        takeUntil(this.destroyed$),
        tap((result: CountedAndPaginated<CommissionTableListItem>) => {
          this.items = result.items;
          this.totalData = result.pagination.total;
        })
      )
      .subscribe();

    this.parseFilters();
  }

  ngOnInit(): void {
  }

  ngAfterViewInit(): void {
    this.paginator.page
      .pipe(
        takeUntil(this.destroyed$)
      )
      .subscribe((event: PageEvent) => {
        this.router.navigate([
          this.activatedRoute.snapshot.routeConfig.path,
          this.filterService.parseFiltersToQueryParams({
            page: event.pageIndex,
            pageSize: event.pageSize,
            ...this.filtersForm.value
          })
        ]);
      });

    this.subscribeQueryParams();
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  parseFilters(filters?: any): void {
    this.initialFilters =
      this.filterService.parseFiltersFromQueryParams({
        uuid: 'string',
        name: 'string',
        shortName: 'string',
        gte$validFrom: 'string',
        lte$validTo: 'string',
      }, filters);
  }
  subscribeQueryParams(): void {
    this.store
      .pipe(
        select(getCurrentRouteState),
        delay(0),
        distinctUntilChanged(
          (prev: RouterStateUrl, curr: RouterStateUrl) =>
            prev.url === curr.url
        ),
        takeUntil(this.destroyed$),
      )
      .subscribe((routerState: RouterStateUrl) => {
        if (this.initialized) {
          this.parseFilters(routerState.params);
        } else {
          this.parseFilters(routerState.params);
          this.initialized = true;
        }

        this.paginator.pageIndex = routerState.params.page;
        this.paginator.pageSize = routerState.params.pageSize;

        this.loadListData();
      });
  }

  loadListData(): void {
    this.commissionTableService.loadList(
      this.paginator.pageIndex,
      this.paginator.pageSize,
      this.initialFilters
    );
  }

  filter(): void {
    this.router.navigate([
      this.activatedRoute.snapshot.routeConfig.path,
      this.filterService.parseFiltersToQueryParams({
        page: 0,
        pageSize: this.paginator.pageSize,
        ...this.filtersForm.value
      })
    ]);
  }

  resetFilters(): void {
    this.filtersForm.resetForm();

    this.router.navigate([
      this.activatedRoute.snapshot.routeConfig.path,
      {
        page: 0,
        pageSize: this.paginator.pageSize,
      }
    ]);
  }

}
